/**
 * Folder helpers shared by client + shooter portals.
 *
 * <FolderSidebar>       — vertical list of folders with counts + create/rename/delete
 * <MoveToFolderMenu>    — kebab popover: move an item into a folder (or "Unfiled")
 * useFolders(scope)     — hook that loads folders + count summary
 * filterByFolder(...)   — client-side filter helper
 *
 * Scopes:
 *   client_clips | client_videos | shooter_photos | shooter_videos
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Folder, FolderPlus, MoreHorizontal, Pencil, Trash2, Check, X, Inbox } from "lucide-react";

export function useFolders(scope) {
  const [folders, setFolders] = useState([]);
  const [unfiledCount, setUnfiledCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!scope) return;
    setLoading(true);
    try {
      const r = await api.get(`/folders?scope=${encodeURIComponent(scope)}`);
      setFolders(r.data.folders || []);
      setUnfiledCount(r.data.unfiled_count || 0);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Failed to load folders");
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  return { folders, unfiledCount, total, loading, reload: load };
}

/**
 * `active` values:
 *   "all"       — every item
 *   "unfiled"   — items with no folder_id
 *   <folder.id> — items in that folder
 */
export function FolderSidebar({ scope, active, onChange, folders, unfiledCount, total, onReload }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameName, setRenameName] = useState("");

  const create = async () => {
    const n = newName.trim();
    if (!n) return;
    try {
      await api.post("/folders", { scope, name: n });
      toast.success("Folder created");
      setNewName("");
      setCreating(false);
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create folder");
    }
  };

  const rename = async (id) => {
    const n = renameName.trim();
    if (!n) return;
    try {
      await api.patch(`/folders/${id}`, { name: n });
      toast.success("Renamed");
      setRenamingId(null);
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rename");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this folder? Items inside will move to Unfiled — media is not deleted.")) return;
    try {
      await api.delete(`/folders/${id}`);
      toast.success("Folder deleted");
      if (active === id) onChange && onChange("all");
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const rowBase = "w-full text-left flex items-center gap-2 px-3 py-2 text-sm border transition-colors";
  const rowActive = "border-orange-600 bg-orange-600/10 text-orange-300";
  const rowIdle = "border-stone-800 hover:border-stone-600 text-stone-300 hover:text-stone-100";

  return (
    <aside className="w-full lg:w-64 shrink-0 space-y-2" data-testid="folder-sidebar">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2 flex items-center justify-between">
        <span>Folders</span>
        <button
          onClick={() => { setCreating(true); setNewName(""); }}
          className="p-1 border border-stone-800 hover:border-orange-600 text-stone-400 hover:text-orange-400"
          title="New folder"
          data-testid="folder-create-btn"
        >
          <FolderPlus size={12} />
        </button>
      </div>

      <button
        onClick={() => onChange && onChange("all")}
        className={`${rowBase} ${active === "all" ? rowActive : rowIdle}`}
        data-testid="folder-tab-all"
      >
        <Inbox size={14} />
        <span className="flex-1">All</span>
        <span className="font-mono-tc text-xs opacity-70">{total}</span>
      </button>

      <button
        onClick={() => onChange && onChange("unfiled")}
        className={`${rowBase} ${active === "unfiled" ? rowActive : rowIdle}`}
        data-testid="folder-tab-unfiled"
      >
        <Folder size={14} />
        <span className="flex-1">Unfiled</span>
        <span className="font-mono-tc text-xs opacity-70">{unfiledCount}</span>
      </button>

      {folders.map((f) => (
        <div key={f.id} className="relative">
          {renamingId === f.id ? (
            <div className="flex items-center gap-1 border border-orange-600 px-2 py-1">
              <input
                autoFocus
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") rename(f.id); if (e.key === "Escape") setRenamingId(null); }}
                className="bg-transparent flex-1 text-sm text-stone-100 focus:outline-none"
                data-testid="folder-rename-input"
                maxLength={80}
              />
              <button onClick={() => rename(f.id)} className="p-1 text-emerald-400 hover:text-emerald-300" data-testid="folder-rename-save">
                <Check size={12} />
              </button>
              <button onClick={() => setRenamingId(null)} className="p-1 text-stone-400 hover:text-stone-100">
                <X size={12} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <button
                onClick={() => onChange && onChange(f.id)}
                className={`${rowBase} flex-1 ${active === f.id ? rowActive : rowIdle}`}
                data-testid={`folder-tab-${f.id}`}
              >
                <Folder size={14} />
                <span className="flex-1 truncate" title={f.name}>{f.name}</span>
                <span className="font-mono-tc text-xs opacity-70">{f.item_count || 0}</span>
              </button>
              <div className="flex items-center">
                <button
                  onClick={() => { setRenamingId(f.id); setRenameName(f.name); }}
                  className="p-1.5 border border-stone-800 hover:border-orange-600 text-stone-400 hover:text-orange-400"
                  title="Rename"
                  data-testid={`folder-rename-${f.id}`}
                >
                  <Pencil size={11} />
                </button>
                <button
                  onClick={() => remove(f.id)}
                  className="p-1.5 border border-stone-800 hover:border-rose-600 text-stone-400 hover:text-rose-400"
                  title="Delete folder"
                  data-testid={`folder-delete-${f.id}`}
                >
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {creating && (
        <div className="flex items-center gap-1 border border-orange-600 px-2 py-1">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") create(); if (e.key === "Escape") setCreating(false); }}
            placeholder="Folder name"
            className="bg-transparent flex-1 text-sm text-stone-100 placeholder-stone-500 focus:outline-none"
            data-testid="folder-new-input"
            maxLength={80}
          />
          <button onClick={create} className="p-1 text-emerald-400 hover:text-emerald-300" data-testid="folder-new-save">
            <Check size={12} />
          </button>
          <button onClick={() => setCreating(false)} className="p-1 text-stone-400 hover:text-stone-100">
            <X size={12} />
          </button>
        </div>
      )}
    </aside>
  );
}

/** Kebab-style popover to move an item to a folder (or unfile it). */
export function MoveToFolderMenu({ scope, itemId, currentFolderId, folders, onMoved, className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const move = async (folderId) => {
    try {
      await api.post("/folders/assign", { scope, item_id: itemId, folder_id: folderId });
      toast.success(folderId ? "Moved to folder" : "Unfiled");
      setOpen(false);
      onMoved && onMoved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to move");
    }
  };

  return (
    <div ref={ref} className={`relative inline-block ${className}`} data-testid={`move-menu-${itemId}`}>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        className="p-1.5 border border-stone-700 hover:border-orange-500 text-stone-400 hover:text-orange-400 transition-colors"
        title="Move to folder"
        data-testid={`move-menu-btn-${itemId}`}
      >
        <Folder size={12} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[180px] bg-stone-950 border border-stone-700 shadow-lg py-1 max-h-72 overflow-y-auto">
          <div className="px-3 py-1 text-[9px] uppercase tracking-widest text-stone-500">Move to</div>
          <button
            onClick={() => move(null)}
            className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-stone-800 ${!currentFolderId ? "text-orange-400" : "text-stone-300"}`}
            data-testid={`move-menu-item-unfiled-${itemId}`}
          >
            <Inbox size={11} /> Unfiled {!currentFolderId && <Check size={10} className="ml-auto" />}
          </button>
          {folders.length === 0 ? (
            <div className="px-3 py-2 text-[10px] text-stone-500 italic">No folders yet — create one from the sidebar.</div>
          ) : (
            folders.map((f) => (
              <button
                key={f.id}
                onClick={() => move(f.id)}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-stone-800 ${currentFolderId === f.id ? "text-orange-400" : "text-stone-300"}`}
                data-testid={`move-menu-item-${f.id}-${itemId}`}
              >
                <Folder size={11} />
                <span className="flex-1 truncate">{f.name}</span>
                {currentFolderId === f.id && <Check size={10} />}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** Filter helper for the currently active folder tab. */
export function filterByFolder(items, active) {
  if (!Array.isArray(items)) return [];
  if (active === "all") return items;
  if (active === "unfiled") return items.filter((it) => !it.folder_id);
  return items.filter((it) => it.folder_id === active);
}
